import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import { filterSchemaByManifest } from '../api/schemas';
import type { TemplateManifest, ContentTypeSchema } from '../types';

/**
 * In-memory cache for template manifests.
 * Keyed by templateId, persists across component renders/unmounts.
 */
const manifestCache = new Map<string, TemplateManifest>();

/**
 * Hook to fetch and cache a template manifest.
 * Returns the manifest and a helper to filter schemas by the manifest's field list.
 *
 * Graceful fallback: if the fetch fails, manifest is null and filterFields
 * returns the schema unchanged (all fields shown — current behavior).
 */
export function useTemplateManifest(templateId: string | undefined) {
  const [manifest, setManifest] = useState<TemplateManifest | null>(
    templateId ? manifestCache.get(templateId) ?? null : null,
  );
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!templateId) return;

    // Already cached
    if (manifestCache.has(templateId)) {
      setManifest(manifestCache.get(templateId)!);
      return;
    }

    // Already fetching this template
    if (fetchedRef.current === templateId) return;
    fetchedRef.current = templateId;

    let cancelled = false;
    setLoading(true);

    api.getTemplateManifest(templateId)
      .then((data) => {
        if (cancelled) return;
        manifestCache.set(templateId, data);
        setManifest(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // Graceful fallback — manifest fetch failure is non-fatal
        console.warn(`Failed to fetch template manifest for ${templateId}:`, err);
        setManifest(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  /**
   * Filter a content type schema by the manifest's field list for that content type.
   * Falls back to the full schema if manifest is unavailable.
   */
  const filterFields = useCallback(
    (contentType: string, schema: ContentTypeSchema): ContentTypeSchema => {
      if (!manifest) return schema;
      const manifestFields = manifest.fields[contentType];
      return filterSchemaByManifest(schema, manifestFields);
    },
    [manifest],
  );

  return { manifest, loading, filterFields };
}
